import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { map } from 'rxjs';
import { AuthService } from '../core/auth.service';
import { I18nService, LANGS, type Lang, TranslatePipe } from '../core/i18n';

interface NavItem {
  path: string;
  icon: string;
  label: string;
  visible: () => boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const THEME_KEY = 'so.theme';

@Component({
  selector: 'app-shell',
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatDividerModule,
    MatTooltipModule,
    TranslatePipe,
  ],
  template: `
    <mat-sidenav-container class="container">
      <mat-sidenav #nav [mode]="isMobile() ? 'over' : 'side'" [opened]="!isMobile()" class="sidenav">
        <div class="brand">
          <span class="logo material-symbols-outlined" aria-hidden="true">recycling</span>
          <div>
            <div class="brand-title">{{ 'app.title' | t }}</div>
            <div class="brand-sub">{{ 'app.subtitle' | t }}</div>
          </div>
        </div>
        @for (g of groups(); track g.label) {
          <div class="group-label">{{ g.label | t }}</div>
          <mat-nav-list>
            @for (item of g.items; track item.path) {
              <a mat-list-item [routerLink]="item.path" routerLinkActive="active" (click)="isMobile() && nav.close()">
                <mat-icon matListItemIcon>{{ item.icon }}</mat-icon>
                <span matListItemTitle>{{ item.label | t }}</span>
              </a>
            }
          </mat-nav-list>
        }
      </mat-sidenav>

      <mat-sidenav-content>
        <mat-toolbar class="toolbar">
          @if (isMobile()) {
            <button mat-icon-button (click)="nav.toggle()" aria-label="Menu"><mat-icon>menu</mat-icon></button>
          }
          <span class="spacer"></span>
          <button mat-icon-button [matMenuTriggerFor]="langMenu" [matTooltip]="'common.language' | t">
            <mat-icon>translate</mat-icon>
          </button>
          <mat-menu #langMenu="matMenu">
            @for (l of langs; track l.code) {
              <button mat-menu-item (click)="setLang(l.code)" [class.selected]="i18n.lang() === l.code">{{ l.label }}</button>
            }
          </mat-menu>
          <button mat-icon-button (click)="toggleTheme()" [matTooltip]="'common.theme' | t">
            <mat-icon>{{ dark() ? 'light_mode' : 'dark_mode' }}</mat-icon>
          </button>
          <button mat-button [matMenuTriggerFor]="userMenu" class="user-btn">
            <mat-icon>account_circle</mat-icon>
            <span class="user-name">{{ auth.user()?.fullName || auth.user()?.email }}</span>
          </button>
          <mat-menu #userMenu="matMenu">
            <div class="user-info">
              <div>{{ auth.user()?.email }}</div>
              <div class="muted">{{ 'role.' + auth.role() | t }}</div>
            </div>
            <mat-divider />
            <button mat-menu-item (click)="auth.logout()"><mat-icon>logout</mat-icon>{{ 'common.logout' | t }}</button>
          </mat-menu>
        </mat-toolbar>
        <main><router-outlet /></main>
      </mat-sidenav-content>
    </mat-sidenav-container>
  `,
  styles: `
    .container { height: 100vh; background: var(--app-bg); }
    .sidenav { width: 264px; border-right: 1px solid var(--grid); background: var(--surface-1); }
    .brand { display: flex; align-items: center; gap: 12px; padding: 20px 16px 12px; }
    .logo { font-size: 32px; color: var(--series-1); }
    .brand-title { font-weight: 700; font-size: 16px; }
    .brand-sub { font-size: 12px; color: var(--text-muted); }
    .group-label { padding: 16px 16px 0; font-size: 11px; text-transform: uppercase; letter-spacing: .06em; color: var(--text-muted); }
    a.active { background: color-mix(in srgb, var(--series-1) 12%, transparent); border-radius: 999px; }
    a.active mat-icon { color: var(--series-1); }
    .toolbar { position: sticky; top: 0; z-index: 3; background: var(--app-bg); border-bottom: 1px solid var(--grid); gap: 4px; }
    .user-name { max-width: 180px; overflow: hidden; text-overflow: ellipsis; display: inline-block; vertical-align: middle; margin-left: 6px; }
    .user-info { padding: 8px 16px 12px; font-size: 13px; }
    .selected { font-weight: 600; }
    @media (max-width: 600px) { .user-name { display: none; } }
  `,
})
export class Shell {
  readonly auth = inject(AuthService);
  readonly i18n = inject(I18nService);
  readonly langs = LANGS;
  readonly isMobile = toSignal(inject(BreakpointObserver).observe('(max-width: 960px)').pipe(map((s) => s.matches)), {
    initialValue: false,
  });
  readonly dark = signal(document.documentElement.classList.contains('dark'));

  readonly groups = computed<NavGroup[]>(() => {
    this.auth.user();
    const staff = () => this.auth.isStaff();
    const all: NavGroup[] = [
      {
        label: 'nav.group.operations',
        items: [
          { path: '/dashboard', icon: 'monitoring', label: 'nav.dashboard', visible: () => this.auth.canSeeDashboard() },
          {
            path: '/appointments',
            icon: 'shopping_bag',
            label: 'nav.appointments',
            visible: () => staff() || this.auth.isCompany() || this.auth.isCollector(),
          },
          { path: '/bags', icon: 'inventory_2', label: 'nav.bags', visible: () => staff() || this.auth.isCollector() },
          {
            path: '/schedules',
            icon: 'event',
            label: 'nav.schedules',
            visible: () => staff() || this.auth.isCompany() || this.auth.isCollector(),
          },
          { path: '/smart-bins', icon: 'sensors', label: 'nav.smartBins', visible: staff },
        ],
      },
      {
        label: 'nav.group.catalog',
        items: [
          { path: '/municipalities', icon: 'location_city', label: 'nav.municipalities', visible: staff },
          { path: '/regions', icon: 'public', label: 'nav.regions', visible: staff },
          { path: '/companies', icon: 'factory', label: 'nav.companies', visible: staff },
          { path: '/recycle-types', icon: 'category', label: 'nav.recycleTypes', visible: staff },
          { path: '/qr-codes', icon: 'qr_code_2', label: 'nav.qrCodes', visible: staff },
        ],
      },
      {
        label: 'nav.group.rewards',
        items: [
          { path: '/rewards', icon: 'redeem', label: 'nav.rewards', visible: staff },
          { path: '/prize-budgets', icon: 'savings', label: 'nav.prizeBudgets', visible: staff },
        ],
      },
      {
        label: 'nav.group.system',
        items: [
          { path: '/users', icon: 'group', label: 'nav.users', visible: staff },
          { path: '/audit', icon: 'history', label: 'nav.audit', visible: () => this.auth.isAdmin() },
        ],
      },
    ];
    return all.map((g) => ({ ...g, items: g.items.filter((i) => i.visible()) })).filter((g) => g.items.length > 0);
  });

  setLang(lang: Lang) {
    this.i18n.set(lang);
  }

  toggleTheme() {
    const dark = !this.dark();
    this.dark.set(dark);
    document.documentElement.classList.toggle('dark', dark);
    try {
      localStorage.setItem(THEME_KEY, dark ? 'dark' : 'light');
    } catch {
      /* ignore */
    }
  }
}

export function applySavedTheme() {
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(THEME_KEY);
  } catch {
    /* ignore */
  }
  const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  document.documentElement.classList.toggle('dark', saved ? saved === 'dark' : !!prefersDark);
}
